import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, ChevronRight, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PathTrail } from "@/components/PathTrail";
import { LocationDialog } from "@/components/LocationDialog";
import { QrPanel } from "@/components/QrPanel";
import { MoveStatusToggle } from "@/components/MoveStatusToggle";
import { ItemCard } from "@/components/ItemCard";
import { KIND_ICON } from "@/pages/Locations";
import { api } from "@/lib/api";
import { NONE, useCategories, useCatLabel, useErrorToast } from "@/lib/hooks";
import { useAuth, useWs } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";

export default function LocationDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const { canWrite } = useWs();
  const showErr = useErrorToast();
  const [dialog, setDialog] = useState(null);
  const { data: loc, isLoading, isError } = useQuery({ queryKey: ["location", id], queryFn: () => api.get(`/locations/${id}`).then((r) => r.data) });
  const { data: cats = NONE } = useCategories();
  const catLabel = useCatLabel();
  const catById = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);

  if (isLoading) return <p className="text-slate-500">{t("common.loading")}</p>;
  if (isError) return <p className="text-slate-500" data-testid="location-not-found">404</p>;

  const remove = async () => {
    if (!window.confirm(t("common.confirmDelete"))) return;
    try {
      await api.delete(`/locations/${id}`);
      qc.invalidateQueries({ queryKey: ["locations"] });
      refresh();
      navigate(loc.parent_id ? `/locations/${loc.parent_id}` : "/locations");
    } catch (e) { showErr(e); }
  };
  const Icon = KIND_ICON[loc.kind];

  return (
    <div className="space-y-8">
      <div className="sticky top-14 lg:top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 py-3 bg-white/80 backdrop-blur-md border-b border-slate-200/60 overflow-x-auto no-scrollbar">
        <PathTrail path={loc.path} size="lg" testId="location-path" />
      </div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 fade-up">
        <div className="space-y-1">
          <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-slate-500"><Icon className="w-4 h-4" />{t(`kind.${loc.kind}`)}</p>
          <h1 data-testid="location-title" className="text-3xl sm:text-4xl font-extrabold">{loc.name}</h1>
        </div>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <Button data-testid="location-add-item" asChild className="h-11 gap-2"><Link to={`/items/new?location=${id}`}><Plus className="w-4 h-4" />{t("item.add")}</Link></Button>
            <Button data-testid="location-add-child" variant="outline" className="h-11 gap-2" onClick={() => setDialog("child")}><Plus className="w-4 h-4" />{t("loc.addChild")}</Button>
            <Button data-testid="location-labels" variant="outline" className="h-11 gap-2" asChild><Link to={`/locations/${id}/labels`}><Printer className="w-4 h-4" />{t("labels.title")}</Link></Button>
            <Button data-testid="location-edit" variant="ghost" size="icon" className="h-11 w-11" onClick={() => setDialog("edit")}><Pencil className="w-4 h-4" /></Button>
            <Button data-testid="location-delete" variant="ghost" size="icon" className="h-11 w-11 text-red-600" onClick={remove}><Trash2 className="w-4 h-4" /></Button>
          </div>
        )}
      </div>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <section className="space-y-3">
            <h2 className="text-lg font-bold">{t("loc.sublocations")} <span className="font-mono text-sm text-slate-400">{loc.children.length}</span></h2>
            {loc.children.length ? (
              <div className="grid sm:grid-cols-2 gap-2">
                {loc.children.map((c) => {
                  const CI = KIND_ICON[c.kind];
                  return (
                    <Link key={c.id} to={`/locations/${c.id}`} data-testid={`child-location-${c.id}`}
                      className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-md">
                      <CI className="w-5 h-5 text-blue-600" />
                      <span className="flex-1 min-w-0"><span className="block font-semibold truncate">{c.name}</span><span className="text-xs text-slate-500">{t("loc.itemsCount", { count: c.item_count })}</span></span>
                      <ChevronRight className="w-4 h-4 text-slate-400 rtl:rotate-180" />
                    </Link>
                  );
                })}
              </div>
            ) : <p className="text-sm text-slate-400">{t("loc.emptyHere")}</p>}
          </section>
          <section className="space-y-3">
            <h2 className="text-lg font-bold">{t("loc.contents")} <span className="font-mono text-sm text-slate-400">{loc.items.length}</span></h2>
            {loc.items.length ? (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="location-items">{loc.items.map((it) => <ItemCard key={it.id} item={it} catLabel={catLabel(catById[it.category_id])} />)}</div>
            ) : <p className="text-sm text-slate-400">{t("loc.emptyHere")}</p>}
          </section>
        </div>
        <div className="space-y-6">
          {loc.kind === "box" && <MoveStatusToggle loc={loc} />}
          <QrPanel loc={loc} />
        </div>
      </div>
      <LocationDialog open={dialog === "child"} onOpenChange={(o) => !o && setDialog(null)} parentId={id} />
      <LocationDialog open={dialog === "edit"} onOpenChange={(o) => !o && setDialog(null)} parentId={loc.parent_id} initial={loc} />
    </div>
  );
}
